/**
 * FilterContext — Flexible hierarchy filter system.
 *
 * Design principles:
 *  1. Division + FiscalYear load the FULL hierarchy once → cached in state.
 *  2. After Division is selected, ALL dropdowns (BDM, ZM, HQ, Brand) are available.
 *  3. Selecting any filter narrows the options of downstream filters using
 *     frontend-only filtering — zero extra API calls.
 *  4. Dependency chain (for narrowing only, not for enabling):
 *       BDM selected  → narrows ZM + HQ options
 *       ZM  selected  → narrows HQ options
 *       HQ  selected  → narrows Brand options
 *  5. Users can pick filters in any order after Division is set.
 *  6. Month (yearmonth) is optional — when set, scopes brand/state/BDM data to MTD.
 */

import {
  createContext, useContext, useState,
  useCallback, useMemo, useEffect, useRef,
} from 'react';
import {
  getDivisions,
  getFiscalYears,
  getBrandSales,
  getBdmHierarchy,
  getStatePerformance,
} from '../services/api';

const FilterContext = createContext(null);

const INITIAL_FILTERS = {
  fiscalYear:      '',
  fiscalYearLabel: '',
  yearmonth:       '',   // optional month filter — "YYYYMM"
  yearmonthLabel:  '',   // e.g. "March 2025"
  division:        '',
  state:           '',
  bdm:             '',
  bdmLabel:        '',
  zm:              '',
  zmLabel:         '',
  hq:              '',
  brand:           '',
};

export const FilterProvider = ({ children }) => {
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  // ── Static option lists (loaded once) ────────────────────────────────────
  const [fiscalYearOptions,  setFiscalYearOptions]  = useState([]);
  const [fiscalYearsLoading, setFiscalYearsLoading] = useState(false);
  const [divisionOptions,    setDivisionOptions]    = useState([]);
  const [divisionsLoading,   setDivisionsLoading]   = useState(false);
  const [stateOptions,       setStateOptions]       = useState([]);
  const [stateLoading,       setStateLoading]       = useState(false);

  // ── Month options — derived from selected fiscal year ─────────────────────
  // fiscalYearOptions shape: [{ id, label, months: [{ id: "YYYYMM", label: "March 2025" }] }]
  // Only show months up to the current month (no future months)
  const monthOptions = useMemo(() => {
    if (!filters.fiscalYear) return [];
    const fy = fiscalYearOptions.find(f => f.id === filters.fiscalYear);
    const allMonths = fy?.months ?? [];

    // Build current year-month as "YYYYMM" for comparison
    const now = new Date();
    const currentYM = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;

    // Filter out months that are in the future
    return allMonths.filter(m => {
      const monthId = String(m.id).replace(/-/g, '');
      return monthId <= currentYM;
    });
  }, [fiscalYearOptions, filters.fiscalYear]);

  // Latest month in the selected fiscal year (used as stockist default when no month selected)
  const latestYearmonth = useMemo(() => {
    const months = monthOptions;
    return months.length ? months[months.length - 1].id : '';
  }, [monthOptions]);

  // ── Full hierarchy — fetched once per (division + fiscalYear) ─────────────
  // Shape: [{ bdm_name, bdm_code, zms: [{ zm_name, zm_code, hqs: [{ hq_name, brands:[] }] }] }]
  const [bdmHierarchy,    setBdmHierarchy]    = useState([]);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);

  // ── Brand list — fetched when division+fiscalYear change ─────────────────
  // (further narrowed in frontend by BDM/ZM/HQ selection)
  const [allBrands,    setAllBrands]    = useState([]);  // full brand list for division+FY
  const [brandLoading, setBrandLoading] = useState(false);

  // ── Flat lookup maps derived from hierarchy (memoised) ───────────────────
  const flatMaps = useMemo(() => {
    const bdmToZms    = new Map();
    const zmToHqs     = new Map();
    const hqToBrands  = new Map();
    const bdmToHqs    = new Map();
    const zmToBrands  = new Map();
    const bdmToBrands = new Map();

    bdmHierarchy.forEach(bdm => {
      const bdmName = bdm.bdm_name;
      if (!bdmName) return;

      if (!bdmToZms.has(bdmName))    bdmToZms.set(bdmName, new Set());
      if (!bdmToHqs.has(bdmName))    bdmToHqs.set(bdmName, new Set());
      if (!bdmToBrands.has(bdmName)) bdmToBrands.set(bdmName, new Set());

      const zms = Array.isArray(bdm.zms) ? bdm.zms : [];
      zms.forEach(zm => {
        const zmName = zm.zm_name;
        if (!zmName) return;

        bdmToZms.get(bdmName).add(zmName);
        if (!zmToHqs.has(zmName))    zmToHqs.set(zmName, new Set());
        if (!zmToBrands.has(zmName)) zmToBrands.set(zmName, new Set());

        const hqs = Array.isArray(zm.hqs) ? zm.hqs : [];
        hqs.forEach(hq => {
          const hqName = hq.hq_name;
          if (!hqName) return;

          bdmToHqs.get(bdmName).add(hqName);
          zmToHqs.get(zmName).add(hqName);
          if (!hqToBrands.has(hqName)) hqToBrands.set(hqName, new Set());

          const brands = Array.isArray(hq.brands) ? hq.brands : [];
          brands.forEach(br => {
            const brName = br.brand_name;
            if (!brName) return;
            hqToBrands.get(hqName).add(brName);
            zmToBrands.get(zmName).add(brName);
            bdmToBrands.get(bdmName).add(brName);
          });
        });
      });
    });

    return { bdmToZms, zmToHqs, hqToBrands, bdmToHqs, zmToBrands, bdmToBrands };
  }, [bdmHierarchy]);

  // ── Load fiscal years on mount ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFiscalYearsLoading(true);
      try {
        const data = await getFiscalYears();
        if (cancelled) return;
        // Store options first, then set the default selection
        setFiscalYearOptions(data);
        if (data.length > 0) {
          setFilters(prev => prev.fiscalYear ? prev : {
            ...prev,
            fiscalYear:      data[0].id,
            fiscalYearLabel: data[0].label,
          });
        }
      } catch (err) {
        console.error('[FilterContext] fiscal-years:', err);
      } finally {
        if (!cancelled) setFiscalYearsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);
  // ── Load divisions on mount ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setDivisionsLoading(true);
      try {
        const data = await getDivisions();
        if (!cancelled) setDivisionOptions(data);
      } catch (err) {
        console.error('[FilterContext] divisions:', err);
      } finally {
        if (!cancelled) setDivisionsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Load full hierarchy when division + fiscalYear are set ────────────────
  useEffect(() => {
    if (!filters.division || !filters.fiscalYear) {
      setBdmHierarchy([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setHierarchyLoading(true);
      try {
        const data = await getBdmHierarchy(filters.division, filters.fiscalYear, filters.yearmonth);
        if (!cancelled) setBdmHierarchy(data);
      } catch (err) {
        console.error('[FilterContext] bdm-hierarchy:', err);
        if (!cancelled) setBdmHierarchy([]);
      } finally {
        if (!cancelled) setHierarchyLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filters.division, filters.fiscalYear, filters.yearmonth]);

  // ── Load ALL brands for division+FY once (no BDM/ZM/HQ params) ───────────
  useEffect(() => {
    if (!filters.division || !filters.fiscalYear) {
      setAllBrands([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setBrandLoading(true);
      try {
        const data = await getBrandSales({
          division:   filters.division,
          fiscalYear: filters.fiscalYear,
          yearmonth:  filters.yearmonth || undefined,
          state:      filters.state || undefined,
        });
        if (!cancelled) setAllBrands(data);
      } catch (err) {
        console.error('[FilterContext] brands:', err);
        if (!cancelled) setAllBrands([]);
      } finally {
        if (!cancelled) setBrandLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filters.division, filters.fiscalYear, filters.yearmonth, filters.state]);

  // Load states for the active division/fiscal year context.
  useEffect(() => {
    if (!filters.division || !filters.fiscalYear) {
      setStateOptions([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setStateLoading(true);
      try {
        const result = await getStatePerformance({
          division:   filters.division,
          fiscalYear: filters.fiscalYear,
          yearmonth:  filters.yearmonth || undefined,
          bdm:        filters.bdm || undefined,
          zm:         filters.zm || undefined,
          hq:         filters.hq || undefined,
        });
        if (cancelled) return;

        const rows = Array.isArray(result) ? result : (result?.rows || []);
        const options = [...new Set(rows
          .map(row => row?.state || row?.State || row?.state_name || row?.stateName)
          .filter(Boolean))]
          .sort()
          .map(state => ({ value: state, label: state, primary: state }));

        setStateOptions(options);
        if (filters.state && !options.some(option => option.value === filters.state)) {
          setFilters(prev => ({ ...prev, state: '' }));
        }
      } catch (err) {
        console.error('[FilterContext] states:', err);
        if (!cancelled) setStateOptions([]);
      } finally {
        if (!cancelled) setStateLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [
    filters.division,
    filters.fiscalYear,
    filters.yearmonth,
    filters.bdm,
    filters.zm,
    filters.hq,
    filters.state,
  ]);

  // ── Derived option lists (pure frontend filtering, zero API calls) ─────────

  // ALL BDMs in the hierarchy
  const bdmOptions = useMemo(() =>
    bdmHierarchy.map(b => ({
      value:   b.bdm_name,
      label:   b.bdm_name,
      primary: b.bdm_name,
      meta:    b.bdm_code || '',
    })),
  [bdmHierarchy]);

  // ZMs — if BDM selected: only ZMs under that BDM; else all ZMs
  const zmOptions = useMemo(() => {
    const { bdmToZms } = flatMaps;
    let zmNames;
    if (filters.bdm) {
      zmNames = [...(bdmToZms.get(filters.bdm) ?? [])];
    } else {
      // All ZMs across all BDMs
      const all = new Set();
      bdmToZms.forEach(set => set.forEach(v => all.add(v)));
      zmNames = [...all];
    }
    return zmNames.sort().map(name => ({
      value: name, label: name, primary: name,
    }));
  }, [flatMaps, filters.bdm]);

  // HQs — narrowed by ZM first, then BDM, then all
  const hqOptions = useMemo(() => {
    const { bdmToHqs, zmToHqs } = flatMaps;
    let hqSet = new Set();

    if (filters.zm) {
      // ZM selected → HQs under that ZM
      const zmHqs = zmToHqs.get(filters.zm);
      if (zmHqs?.size) {
        zmHqs.forEach(h => hqSet.add(h));
      } else if (filters.bdm) {
        // ZM not found in map — fall back to BDM scope
        (bdmToHqs.get(filters.bdm) ?? new Set()).forEach(h => hqSet.add(h));
      }
    } else if (filters.bdm) {
      // BDM selected (no ZM) → all HQs under that BDM
      (bdmToHqs.get(filters.bdm) ?? new Set()).forEach(h => hqSet.add(h));
    } else {
      // Nothing selected → all HQs across entire hierarchy
      zmToHqs.forEach(set => set.forEach(h => hqSet.add(h)));
    }

    return [...hqSet].sort().map(name => ({
      value: name, label: name, primary: name,
    }));
  }, [flatMaps, filters.bdm, filters.zm]);

  // Brands — narrowed by HQ, ZM, or BDM selection (frontend only)
  const brandOptions = useMemo(() => {
    const { hqToBrands, zmToBrands, bdmToBrands } = flatMaps;

    let allowedNames = null; // null = no restriction

    if (filters.hq) {
      allowedNames = hqToBrands.get(filters.hq) ?? new Set();
    } else if (filters.zm) {
      allowedNames = zmToBrands.get(filters.zm) ?? new Set();
    } else if (filters.bdm) {
      allowedNames = bdmToBrands.get(filters.bdm) ?? new Set();
    }

    return allBrands
      .filter(b => !allowedNames || allowedNames.has(b.brand_name))
      .map(b => ({
        value:   b.brand_name,
        label:   b.brand_name,
        primary: b.brand_name,
        sub:     b.total_sales != null ? `${(+b.total_sales / 100000).toFixed(1)}L` : null,
      }));
  }, [allBrands, flatMaps, filters.bdm, filters.zm, filters.hq]);

  // ── Filter update with smart cascade resets ───────────────────────────────
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value };

      // Division change → reset everything downstream
      if (key === 'division') {
        next.state = '';
        next.bdm = ''; next.bdmLabel = '';
        next.zm  = ''; next.zmLabel  = '';
        next.hq  = ''; next.brand    = '';
      }
      // FiscalYear change → reset hierarchy-dependent filters + month
      if (key === 'fiscalYear') {
        next.yearmonth      = '';
        next.yearmonthLabel = '';
        next.state = '';
        next.bdm = ''; next.bdmLabel = '';
        next.zm  = ''; next.zmLabel  = '';
        next.hq  = ''; next.brand    = '';
      }
      // BDM change → reset ZM, HQ (brand stays unless invalid)
      if (key === 'bdm') {
        next.zm = ''; next.zmLabel = '';
        next.hq = '';
      }
      // ZM change → reset HQ
      if (key === 'zm') {
        next.hq = '';
      }
      // HQ change → reset brand
      if (key === 'hq') {
        next.brand = '';
      }

      return next;
    });
  }, []);

  const updateFilters = useCallback((updates) => {
    setFilters(prev => {
      const next = { ...prev, ...updates };

      if ('division' in updates) {
        next.state = '';
        next.bdm = ''; next.bdmLabel = '';
        next.zm  = ''; next.zmLabel  = '';
        next.hq  = ''; next.brand    = '';
      }
      if ('fiscalYear' in updates) {
        next.yearmonth      = '';
        next.yearmonthLabel = '';
        next.state = '';
        next.bdm = ''; next.bdmLabel = '';
        next.zm  = ''; next.zmLabel  = '';
        next.hq  = ''; next.brand    = '';
      }
      if ('bdm' in updates) {
        next.zm = ''; next.zmLabel = '';
        next.hq = '';
      }
      if ('zm' in updates) {
        next.hq = '';
      }
      if ('hq' in updates) {
        next.brand = '';
      }

      return next;
    });
  }, []);

  const resetFilters = useCallback(() => setFilters(INITIAL_FILTERS), []);

  const hasActiveFilters = useMemo(
    () => !!(filters.division && filters.fiscalYear),
    [filters.division, filters.fiscalYear]
  );

  return (
    <FilterContext.Provider value={{
      filters,
      updateFilter,
      updateFilters,
      resetFilters,
      hasActiveFilters,

      fiscalYearOptions,
      fiscalYearsLoading,
      divisionOptions,
      divisionsLoading,
      stateOptions,
      stateLoading,

      monthOptions,
      latestYearmonth,

      bdmOptions,
      bdmLoading: hierarchyLoading,

      zmOptions,
      zmLoading: hierarchyLoading,

      hqOptions,
      hqLoading: false,

      brandOptions,
      brandLoading,

      // Expose hierarchy loading state for UI indicators
      hierarchyLoading,
    }}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilters = () => {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilters must be used within FilterProvider');
  return ctx;
};
